import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOff";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

const KIND_META = {
  no_permission: { severity: "error", Icon: LockOutlinedIcon },
  not_found: { severity: "error", Icon: SearchOffOutlinedIcon },
  excluded: { severity: "warning", Icon: ArchiveOutlinedIcon },
  load_error: { severity: "error", Icon: ErrorOutlineOutlinedIcon },
  invalid_id: { severity: "error", Icon: ErrorOutlineOutlinedIcon },
};

/**
 * Painel único de orientação quando o workspace não está disponível para consulta.
 */
export default function WorkspaceUnavailablePanel({
  unavailable,
  listPath,
  governancePath,
  canOpenGovernance = false,
  onRetry,
  compact = false,
}) {
  const theme = useTheme();
  const { t } = useTranslation("app");
  if (!unavailable?.kind) return null;

  const meta = KIND_META[unavailable.kind] || KIND_META.load_error;
  const Icon = meta.Icon;
  const host = unavailable.host || "";
  const id = unavailable.dominioId || "";
  const titleKey = `platformPage.workspaceUnavailable.${unavailable.kind}.title`;
  const bodyKey = `platformPage.workspaceUnavailable.${unavailable.kind}.body`;
  const hintKey = `platformPage.workspaceUnavailable.${unavailable.kind}.hint`;

  const title = t(titleKey, { host: host || `ID ${id}`, id, defaultValue: "" });
  const body = t(bodyKey, { host: host || `ID ${id}`, id, defaultValue: "" });
  const hint = t(hintKey, { defaultValue: "" });

  return (
    <Paper
      variant="outlined"
      sx={{
        p: compact ? 2 : { xs: 2, sm: 2.5 },
        mb: 2,
        borderWidth: 2,
        borderColor: (th) =>
          unavailable.kind === "excluded"
            ? th.palette.warning.main
            : th.palette.error.main,
        bgcolor: (th) =>
          unavailable.kind === "excluded"
            ? th.palette.mode === "dark"
              ? "rgba(237, 108, 2, 0.08)"
              : "rgba(237, 108, 2, 0.06)"
            : th.palette.mode === "dark"
              ? "rgba(211, 47, 47, 0.08)"
              : "rgba(211, 47, 47, 0.04)",
      }}
    >
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 48,
            height: 48,
            borderRadius: 2,
            flexShrink: 0,
            bgcolor:
              unavailable.kind === "excluded"
                ? alpha(theme.palette.warning.main, 0.12)
                : alpha(theme.palette.error.main, 0.1),
            color: unavailable.kind === "excluded" ? "warning.main" : "error.main",
          }}
        >
          <Icon fontSize="medium" />
        </Box>
        <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant={compact ? "subtitle1" : "h6"} fontWeight={700} component="h2">
            {title}
          </Typography>
          {body ? (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55, maxWidth: 720 }}>
              {body}
            </Typography>
          ) : null}
          {hint ? (
            <Alert severity={unavailable.kind === "excluded" ? "warning" : "info"} variant="outlined" sx={{ mt: 0.5 }}>
              {hint}
            </Alert>
          ) : null}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap" useFlexGap sx={{ pt: 0.5 }}>
            {listPath ? (
              <Button
                component={RouterLink}
                to={listPath}
                variant="contained"
                size="small"
                startIcon={<ArrowBackIcon />}
                color={unavailable.kind === "excluded" ? "warning" : "primary"}
              >
                {t("platformPage.workspaceUnavailable.backToList")}
              </Button>
            ) : null}
            {unavailable.kind === "no_permission" && canOpenGovernance && governancePath ? (
              <Button
                component={RouterLink}
                to={governancePath}
                variant="outlined"
                size="small"
                startIcon={<AdminPanelSettingsOutlinedIcon />}
              >
                {t("platformPage.workspaceUnavailable.openGovernance")}
              </Button>
            ) : null}
            {unavailable.kind === "load_error" && typeof onRetry === "function" ? (
              <Button variant="outlined" size="small" onClick={onRetry}>
                {t("platformPage.workspaceUnavailable.retry")}
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
}
